import type { Report, ReportRange } from '@ofmos/shared/report';
import { REPORT_RANGES } from '@ofmos/shared/report';
import type { ObjectMetadata } from '../../data/client';
import type { SegmentDef } from '../../ui';

/**
 * WHAT THE REPORTS SCREEN AGREES WITH ITSELF ABOUT (`spec/screens/reports.md` section 2).
 *
 * Three panes and two viewports read the same report list, the same range vocabulary and
 * the same takeaway; a second copy of any of them in a second file is how the list pane
 * and the phone list come to group by different names.
 */

/**
 * THE RANGE CONTROL'S SEGMENTS, AND THEY ARE THE FIVE THE DEFINITION CAN STORE.
 *
 * `spec/screens/reports.md` section 2 item 2 draws the control as "7 days, 30 days,
 * 90 days, Custom..."; `docs/decisions/reports.md` R3 closed the stored vocabulary at
 * these five and says "No custom from/to range in this round" in as many words, and
 * `reportRangeSchema` (packages/shared) is what a stored definition is validated against
 * on write AND on read. Relabelling the control alone would make it a control that lies;
 * changing the vocabulary is a change to `packages/shared` and to the server's own
 * `resolveReportWindow`, neither of which this slice owns. Ledger E-W4.
 */
const RANGE_LABELS: Record<ReportRange, string> = {
  last_30_days: 'Last 30 days',
  this_quarter: 'This quarter',
  last_12_months: 'Last 12 months',
  this_year: 'This year',
  all_time: 'All time',
};

export function rangeLabel(range: ReportRange): string {
  return RANGE_LABELS[range];
}

/**
 * THE SEGMENT'S LABEL IS SHORTER THAN THE SENTENCE'S, and the reason is the toolbar rather
 * than taste: five segments reading "Last 30 Days ... Last 12 Months" make a control wide
 * enough to push the toolbar's search capsule onto a second line at 1440 (driven in the
 * browser, not reasoned about). The spec draws the control with labels of exactly this
 * length - "7 days, 30 days, 90 days" - so the short form is also the nearer one. The long
 * form stays where it is read as prose: the takeaway sentence.
 */
const RANGE_SEGMENT_LABELS: Record<ReportRange, string> = {
  last_30_days: '30 days',
  this_quarter: 'Quarter',
  last_12_months: '12 months',
  this_year: 'Year',
  all_time: 'All time',
};

export const RANGE_SEGMENTS: readonly SegmentDef<ReportRange>[] = REPORT_RANGES.map((range) => ({
  value: range,
  label: RANGE_SEGMENT_LABELS[range],
}));

/**
 * Chart or Table, the choice the spec puts first in the toolbar's centre area.
 *
 * WORDS, NOT SYMBOLS, and the toolbar is the reason again: two segments with a symbol AND
 * a word cost about forty pixels more than two with a word, and the centre area is sharing
 * a 1091px bar with a window title and three trailing items at 1440. The kit ships the
 * toolbar's segmented control with either (`Titlebars and Toolbars/Light/XL/Segmented
 * Control/Active/4 Items/1 Selected` is a text control), so this is a fit decision rather
 * than a construction one - and "Chart" and "Table" are the two words a reader needs.
 */
export type ReportView = 'chart' | 'table';

export const VIEW_SEGMENTS: readonly SegmentDef<ReportView>[] = [
  { value: 'chart', label: 'Chart' },
  { value: 'table', label: 'Table' },
];

export interface ReportGroup {
  readonly objectId: string;
  /** The object's own plural label, or a name for reports whose object has gone. */
  readonly label: string;
  readonly object: ObjectMetadata | undefined;
  readonly reports: readonly Report[];
}

/**
 * REPORTS GROUPED BY OBJECT, in the objects' own alphabetical order (section 2 item 3:
 * "reports grouped by object with a group header per object"). Somebody looking for a
 * report thinks "the deal one", and eight seeded reports plus whatever a team builds is
 * already past the length one flat list stays scannable at.
 */
export function groupReports(
  reports: readonly Report[],
  objects: readonly ObjectMetadata[],
): readonly ReportGroup[] {
  const byId = new Map(objects.map((object) => [object.id, object]));
  const groups = new Map<string, Report[]>();
  for (const report of reports) {
    const key = report.definition.objectId;
    groups.set(key, [...(groups.get(key) ?? []), report]);
  }
  return [...groups.entries()]
    .map(([objectId, entries]) => ({
      objectId,
      object: byId.get(objectId),
      /**
       * A report whose object was deleted still has a row: hiding it would make the
       * report unreachable and undeletable, which is worse than one group called what
       * the reader can see it is.
       */
      label: byId.get(objectId)?.labelPlural ?? 'Other',
      reports: [...entries].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** The number formatter every pane on this screen shares. */
export function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}
